import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { secureDelete, secureGetPdf, secureGet } from "../../config/api";
import { formatDateTime, formatAmount } from "../../utils/format";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import ProductRow from "../../components/ProductRow";
import DeletePopup from "../../components/DeletePopup";
import SnackbarNotification from '../../components/SnackbarNotification';
import Breadcrumbs from "../../components/Breadcrumbs";
import LoadingLogo from "../../components/LoadingLogo";

import trashIcon from "../../icons/trashW.svg";
import exportIcon from "../../icons/export.svg";

const InvoiceDetails = () => {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const { id } = useParams();

  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const [invoice, setInvoice] = useState({});
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const fetchInvoice = useCallback(async () => {
    setLoading(true);
    try {
      const response = await secureGet(`/invoices/${id}`, showSnackbar, { signal });
      const fetchedInvoice = response.data;
      setInvoice(fetchedInvoice);
      console.log("fetchedInvoice", fetchedInvoice);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Failed to fetch order:", error);
      }
    } finally {
      setLoading(false);
    }
  }, [id, signal]);

  useEffect(() => {
    fetchInvoice();

    return () => {
      abortController.abort();
    };
  }, [id, abortController, fetchInvoice]);

  const quotesExist = invoice.deliveryNotes?.[0]?.carts?.[0]?.quotes && invoice.deliveryNotes?.[0]?.carts?.[0]?.quotes.length > 0;

  const renderStatut = () => (
    <div className="flex items-center ">
      <span
        className={`px-[5px] py-1 rounded-[10px] font-semibold text-[12px] ${quotesExist ? "bg-[#0AD69F] text-white cursor-pointer" : "bg-[#EF476F] text-white cursor-not-allowed"
          }`}
        onClick={() => quotesExist && handleStatutClick("DV")}
      >
        DV
      </span>
      <div className="w-2 h-[2px] bg-[#043F88]"></div>


      <span
        className="px-[5px] py-1 rounded-[10px] font-semibold text-[12px] bg-[#0AD69F] text-white cursor-pointer"
        onClick={() => handleStatutClick("BC")}
      >
        BC
      </span>
      <div className="w-2 h-[2px] bg-[#043F88]"></div>

      <span
        className="px-[5px] py-1 rounded-[10px] font-semibold text-[12px] bg-[#0AD69F] text-white cursor-pointer"
        onClick={() => handleStatutClick("BL")}
      >
        BL
      </span>
      <div className="w-2 h-[2px] bg-[#043F88]"></div>

      <span
        className="px-[5px] py-1 rounded-[10px] font-semibold text-[12px] bg-[#0AD69F] text-white cursor-pointer"
      >
        FA
      </span>
    </div>
  );

  const handleStatutClick = (key) => {
    switch (key) {
      case "DV":
        handleConsultDV();
        break;
      case "BC":
        handleConsultBC();
        break;
      case "BL":
        handleConsultBL();
        break;
      default:
        break;
    }
  };

  const handleConsultDV = () => {
    history.push({
      pathname: `/quotes/details/${invoice.deliveryNotes?.[0]?.carts?.[0]?.quotes.id}`,
    });
  };

  const handleConsultBC = () => {
    history.push({
      pathname: `/orders/details/${invoice.deliveryNotes?.[0]?.carts?.[0]?.id}`,
    });
  };

  const handleConsultBL = () => {
    history.push({
      pathname: `/delivery-notes/details/${invoice.deliveryNotes?.[0]?.id}`,
    });
  };

  const handleDeleteClick = () => setDeletePopupVisible(true);

  const handleDeleteConfirm = async () => {
    try {
      await secureDelete(`/invoices/delete/${invoice.id}`, showSnackbar);
      setDeletePopupVisible(false);
      history.push({
        pathname: "/invoices",
        state: {
          showSnackbar: true,
          snackbarMessage: t(`confirmation.invoiceDelete`),
          snackbarSeverity: 'success',
          filters: location.state?.filters || {},
          page: location.state?.page || 0,
          size: location.state?.size || 30,
        },
      });
    } catch (error) {
      setDeletePopupVisible(false);
      console.error("Error deleting invoice:", error);
    }
  };

  const handleDownloadClick = async () => {
    try {
      const response = await secureGetPdf(`/invoices/${invoice.id}/pdf`, showSnackbar);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${invoice.invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showSnackbar(t('confirmation.downloadSuccess'), 'success');
    } catch (error) {
      console.error("Failed to download invoice:", error);
    }
  };

  const renderActionButtons = () => (
    <>
      <button
        className="flex items-center justify-center bg-white text-[#043F88] px-6 py-3 font-bold text-[14px] rounded-[12px] gap-x-2 shadow-[0px_20px_80px_#00000011]"
        onClick={handleDownloadClick}
      >
        <img src={exportIcon} alt="download" className="w-5 h-5" />
        {t('actions.downloadInvoice')}
      </button>
      <button
        className="flex items-center justify-center bg-[#EF476F] text-white px-6 py-3 font-bold text-[14px] rounded-[12px] gap-x-2 shadow-[0px_20px_80px_#00000011]"
        onClick={handleDeleteClick}
      >
        <img src={trashIcon} alt="delete" className="w-5 h-5" />
        {t('actions.delete')}
      </button>
    </>
  );

  if (loading) return <LoadingLogo />;


  return (
    <div className="">
      <Helmet>
        <title>GrayFox - {t("titles.invoice_details")}</title>
      </Helmet>

      <div className="mb-4">
        <Breadcrumbs />
        <h1 className="text-[40px] font-semibold text-black">
          {t('titles.invoice_details')}
        </h1>

        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-[10px]">
            <p className="text-black font-semibold text-[24px]">
              {invoice.invoiceNumber}
            </p>
            <div className="flex space-x-1">{renderStatut()}</div>
          </div>
          <div className="flex space-x-4">{renderActionButtons()}</div>
        </div>

        <div>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {invoice.client?.code} - {invoice.client?.name}
          </p>
        </div>
      </div>

      <div className="w-full flex justify-between bg-white py-4 pl-[32px] rounded-[12px] shadow-[0px_6px_26px_#626B7D12] mb-4">
        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">
            {t('rows.creationDate')}
          </p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {formatDateTime(invoice.createdAt)}
          </p>
        </div>

        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">
            {t('rows.commercial')}
          </p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {invoice.user?.firstName} {invoice.user?.lastName}
          </p>
        </div>

        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">
            STOCK
          </p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {invoice.deliveryNotes?.[0]?.carts?.[0].repo?.name}
          </p>
        </div>

        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">
            {t(`rows.paymentMode`)}
          </p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {invoice.deliveryNotes?.[0]?.carts?.[0]?.paymentMode}
          </p>
        </div>

        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">TOTAL HT</p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {formatAmount(invoice.priceHT)}
          </p>
        </div>

        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">TOTAL TTC</p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {formatAmount(invoice.priceTTC)}
          </p>
        </div>
      </div>

      <h3 className="text-[16px] font-medium text-[#ACAFC6] mb-4">
        {t('fields_types.list')}
      </h3>

      <div className="space-y-2">
        {invoice.deliveryNotes?.[0]?.carts?.[0].details?.map((product, index) => (
          <ProductRow key={index} product={product} />
        ))}
      </div>

      {isDeletePopupVisible && (
        <DeletePopup
          open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </div>
  );
};

export default InvoiceDetails;
